import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Box, Card, Flex, Group, Image, Text } from '@mantine/core';
import { IconGripVertical, IconPencil, IconTrash } from '@tabler/icons-react';
import { isValidCardBtnUrl, isValidCardImageUrl } from '../../helpers/whatsappCarouselValidation';

const CardThumbnail = ({ imageUrl }) => {
  if (!imageUrl) {
    return (
      <Box
        style={{
          width: 80,
          height: 80,
          borderRadius: 4,
          backgroundColor: 'var(--mantine-color-gray-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Text size="xs" c="dimmed">
          No image
        </Text>
      </Box>
    );
  }

  return (
    <Image
      src={imageUrl}
      w={80}
      h={80}
      fit="cover"
      radius={4}
      style={{ flexShrink: 0, border: '1px solid var(--mantine-color-gray-3)' }}
    />
  );
};

const SortableCardItem = ({ card, index, onEdit, onDelete, countLocked }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: card._sortId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // The form's Save gate runs the same isValidCard checks - without a row-level message the
  // user would face a disabled Save with nothing on screen to fix. Pre-invalid cards are
  // reachable without ever opening the editor, e.g. "Make a copy" of a template whose stored
  // image URL has no protocol.
  const cardError = !isValidCardImageUrl(card.imageUrl)
    ? 'Invalid image URL - a full https:// address is required.'
    : !isValidCardBtnUrl(card.btnUrl)
      ? 'Invalid button URL - use a full https:// address or a merge tag.'
      : null;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card mb="xs" withBorder style={cardError ? { borderColor: 'var(--mantine-color-red-6)' } : undefined}>
        <Flex gap="sm" align="center">
          {!countLocked && (
            <Box {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
              <IconGripVertical size={18} color="var(--mantine-color-dimmed)" />
            </Box>
          )}
          <CardThumbnail imageUrl={card.imageUrl} />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={500} size="sm">
              Card {index + 1}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {card.btnUrl || 'No button URL yet'}
            </Text>
            {cardError && (
              <Text size="xs" c="red">
                {cardError}
              </Text>
            )}
          </Box>
          <Group gap="xs">
            <ActionIcon variant="subtle" onClick={() => onEdit(index)} aria-label={`Edit card ${index + 1}`}>
              <IconPencil size={16} />
            </ActionIcon>
            {!countLocked && (
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => onDelete(index)}
                aria-label={`Delete card ${index + 1}`}
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        </Flex>
      </Card>
    </div>
  );
};

// Sortable list of static carousel cards (same interaction pattern as Stories slides).
// countLocked hides delete and drag-reorder: Meta fixes the card count at template creation,
// so a saved template's cards can be edited but never added, removed, or (for clarity) reordered.
const WhatsappCarouselCardList = ({ cards, onEdit, onDelete, onReorder, countLocked }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // `_sortId` is assigned by the parent form when a card is created and travels with the card, so
  // it stays stable across reorders and deletes (an index-derived id would be reassigned to a
  // different card mid-drag). The parent strips it before submitting.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = cards.findIndex((c) => c._sortId === active.id);
    const newIndex = cards.findIndex((c) => c._sortId === over.id);
    onReorder(arrayMove(cards, oldIndex, newIndex));
  };

  if (cards.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="md">
        No cards yet. Add at least 2 cards (up to 10).
      </Text>
    );
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={cards.map((c) => c._sortId)} strategy={verticalListSortingStrategy}>
          {cards.map((card, index) => (
            <SortableCardItem
              key={card._sortId}
              card={card}
              index={index}
              onEdit={onEdit}
              onDelete={onDelete}
              countLocked={countLocked}
            />
          ))}
        </SortableContext>
      </DndContext>
      {/* A single card blocks the parent's Save gate, but the one row on screen looks perfectly
          healthy - say why. (Zero cards is covered by the empty state above.) */}
      {cards.length < 2 && (
        <Text size="xs" c="red">
          A carousel needs at least 2 cards.
        </Text>
      )}
    </>
  );
};

export default WhatsappCarouselCardList;
